import React, { useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
// import { Grid, LinearProgress } from "@material-ui/core";
import './PDFViewer.scss';
import { formatFileSize } from "../../utils/helper";
import Loader from "../Loader/Loader";
import images from "../../assets/images";
import { isPlatform } from "@ionic/react";
import { ElectronFileDetail, CordovaFileDetail } from "../../utils/interface";
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Tooltip } from "@mui/material";

/*
unpkg is a fast, global content delivery network for everything on npm. 
Use it to quickly and easily load any file from any package using a URL 
*/
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface Config {
  fullLoading: boolean;
  numPages: number | null;
  scale: number;
  pageNumber: number;
  page: number;
  minScale: number;
  maxScale: number;
  scalePoint: number;
}

interface material {
  filename: string;
  filepath: string;
  filesize: number;
  isclassroom: boolean;
  coverimagepath?: string;
  authorname?: string;
};

interface PDFViewerProps {
  file: string;
  pdfFileData: material | undefined | CordovaFileDetail | ElectronFileDetail| null;
  onClose: () => void;
  type: string;
}

interface LoadEvent {
  numPages: number;
}

const CreatePages = (numPages: number | null, scale: number, page: number, onPageRender: () => void) => {
  const pages: JSX.Element[] = [];
  if (numPages === null) return pages;

  for (let i = page; i <= numPages; i++) {
    if (i > numPages) break;
    pages.push(
      <Page
        key={`page-${i}`}
        renderMode="canvas"
        loading={<Loader />}
        pageNumber={i}
        width={scale}
        renderTextLayer={true}
        renderAnnotationLayer={true}
        onRenderSuccess={onPageRender}
      />
    );
  }

  return pages;
};

const PDFViewer: React.FC<PDFViewerProps> = ({ file, pdfFileData, onClose, type }) => {
  const { guideDefaultImage } = images;
  const baseUrl = process.env.REACT_APP_FRONTEND_URL;
  console.log('file inside the pdf viewer=========>',file);
  const fileOrBase64 = file.toLowerCase().endsWith(".pdf")
    ? file
    : `data:application/pdf;base64,${file}`;
  const [isDocumentLoading, setIsDocumentLoading] = useState<boolean>(true);
  const [pagesRendered, setPagesRendered] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [config, setConfig] = useState<Config>({
    fullLoading: false,
    numPages: null,
    scale: window.innerWidth * 0.5,
    pageNumber: 10,
    page: 1,
    minScale: 500,
    maxScale: window.innerWidth - 50,
    scalePoint: 40
  });

  const onDocumentLoadSuccess = ({ numPages }: LoadEvent) => {
    setConfig({ ...config, numPages, pageNumber: numPages, fullLoading: true });
    setPagesRendered(0); // Reset pages rendered count
    setIsDocumentLoading(true); // Continue loading until all pages are rendered
    setError(null);
  };

  // Called when each individual page is rendered
  const onPageRenderSuccess = () => {
    setPagesRendered((prevRendered) => {
      const newRendered = prevRendered + 1;
      if (newRendered === config.numPages) {
        setIsDocumentLoading(false); // Stop loading when all pages are rendered
      }
      return newRendered;
    });
  };

  const onDocumentLoadError = (error: Error) => {
    setError(error.message); // Set error message
    setIsDocumentLoading(false); // Stop loading in case of error
  };

  useEffect(() => {
    const handleResize = () => {
      setConfig(prevConfig => ({
        ...prevConfig,
        scale: window.innerWidth * 0.5,
        maxScale: window.innerWidth - 50
      }));
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const textRef = useRef<HTMLDivElement>(null);
  const [isEllipsisActive, setIsEllipsisActive] = useState(false);

  // Function to check if text is truncated
  const checkEllipsis = useCallback(() => {
    if (textRef.current) {
      setIsEllipsisActive(textRef.current.scrollWidth > textRef.current.clientWidth);
    }
  }, []);

  useEffect(() => {
    checkEllipsis(); // Check on mount
    window.addEventListener("resize", checkEllipsis); // Recheck on resize

    return () => window.removeEventListener("resize", checkEllipsis); // Cleanup on unmount
  }, [checkEllipsis, pdfFileData?.filename]);


  const filenameRef = useRef<HTMLDivElement>(null);
  const authorRef = useRef<HTMLDivElement>(null);
  const [isFilenameOverflow, setIsFilenameOverflow] = useState(false);
  const [isAuthorOverflow, setIsAuthorOverflow] = useState(false);

  const checkOverflow = (ref: React.RefObject<HTMLDivElement>, setOverflow: (value: boolean) => void) => {
    if (ref.current) {
      setOverflow(ref.current.scrollWidth > ref.current.clientWidth);
    }
  };


  return (
    <div className={type === "Examination Guide" ? "pdfOuterGuideContainer" :type === "Assignment" ? "pdfOuterAssignmentContainer" : type === 'chat' ? "chatPdfContainer" :"pdfContainer"}>
      <div className={type === "Examination Guide" ? "pdfGuideContainer" : error ? "pdfInnerContainer" : ""}>
        {type === "Examination Guide" ?
          <div className="PdfHeaderCtn">
            <img src={pdfFileData?.coverimagepath ? baseUrl + pdfFileData?.coverimagepath : guideDefaultImage} alt="guide-img" className="guideImage" />
            <div className="PdfHeaderGuide">
            <Tooltip title={pdfFileData?.filename} disableHoverListener={!isFilenameOverflow}>
        <div
          ref={filenameRef}
          className="guideTitle"
          style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          onMouseEnter={() => checkOverflow(filenameRef, setIsFilenameOverflow)}
        >
          {pdfFileData?.filename}
        </div>
      </Tooltip>

      {/* Author Tooltip */}
      <Tooltip title={pdfFileData?.authorname} disableHoverListener={!isAuthorOverflow}>
        <div
          ref={authorRef}
          className="author"
          style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          onMouseEnter={() => checkOverflow(authorRef, setIsAuthorOverflow)}
        >
          {pdfFileData?.authorname}
        </div>
      </Tooltip>
            </div>
          </div>
          : (type === "Questions bank" || type === 'chat' || type === 'system_messages') ?
            <></>
            :
            <div className="PdfHeader">
<Tooltip title={isEllipsisActive ? pdfFileData?.filename : ""}>
                <div ref={textRef} className="fileName">
                  {pdfFileData?.filename}
                </div>
                </Tooltip>              
                <div className="fileSize">Size: {pdfFileData?.filesize ? formatFileSize(pdfFileData?.filesize) : '0 Mb'}</div>
            </div>
        }
        {isDocumentLoading && <Loader />}
        <div style={{ display: isDocumentLoading ? "none" : undefined }} className={(type === "Examination Guide" && error) ? "guideContent errorContent" : type === "Examination Guide" ? "guideContent" : "content"}>
          <div >
            {error ? (
              <div className="errorContainer">
                <p className="errorMessage">{error}</p>
              </div>
            ) : (
              <Document
                file={(isPlatform('android')||(window?.electron && window.electron.isElectron())) ? file : fileOrBase64}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<Loader bottom="50px" />}
                className={"gridItem"}
                externalLinkTarget="_blank"
              >
                {CreatePages(config.numPages, config.scale, config.page, onPageRenderSuccess)}
              </Document>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
